import { t } from 'i18next';
import { toast } from 'react-toastify';
import { UnitStatusKey } from '~/providers/types/unit';
import { apiCall } from '../providers';
import { getCachedDevInfo } from './cachedStorage';
import { getUserProfile, Profile } from './userProfile';

export function handleUnitStatusByProfileInstallation(profile: Profile): UnitStatusKey[] {
  const unitStatus: UnitStatusKey[] = ['OPERATION', 'MODERNIZATION', 'FORCED_UNINSTALL'];

  if (profile.manageAllClients || profile.permissions.isInstaller) {
    unitStatus.push('INSTALLATION');
  }

  return unitStatus;
}

export function setValueState(key: string): string[] {
  const profile = getUserProfile();
  if (profile.permissions.isAdminSistema) {
    return [];
  }

  const value = sessionStorage.getItem(key);

  if (value) {
    const splittedValue = value.split(',');
    return splittedValue;
  }
  return [];
}

export async function getCitiesList(state, render): Promise<void> {
  try {
    const { list: citiesList } = await apiCall('/dac/get-cities-list', {});
    state.citiesListOpts = citiesList.map((city) => ({
      value: city.id.toString(),
      name: city.name,
      stateId: city.stateId,
    }));
    state.selectedCityOpts = setValueState('filterCity');
    render();
  } catch (err) {
    console.log(err);
    toast.error(t('ErroCidades'));
  }
}

export async function getUnitsList(state, render): Promise<void> {
  try {
    const profile = getUserProfile();

    const { list: unitsList } = await apiCall('/clients/get-units-list-basic', {
      INCLUDE_UNIT_STATUS: handleUnitStatusByProfileInstallation(profile),
    });

    state.unitsListOpts = unitsList.map((unit) => ({
      value: unit.UNIT_ID.toString(),
      name: unit.UNIT_NAME,
      clientId: unit.CLIENT_ID,
      city: unit.CITY_ID,
      state: unit.STATE_ID,
    }));
    state.selectedUnitOpts = setValueState('filterUnit');
    render();
  } catch (err) {
    console.log(err);
    toast.error(t('erroUnidades'));
  }
}

export async function getMachinesList(state, render): Promise<void> {
  try {
    if (!state.selectedClientOpts?.length && !state.selectedUnitOpts?.length) return;
    const profile = getUserProfile();
    const { list: machineList } = await apiCall('/clients/get-machines-list-basic', {
      CLIENT_IDS: state.selectedClientOpts,
      UNIT_IDS: state.selectedUnitOpts,
      INCLUDE_UNIT_STATUS: handleUnitStatusByProfileInstallation(profile),
    });
    state.machinesListOpts = machineList;
    render();
  } catch (err) {
    console.log(err);
    toast.error(t('erroMaquinas'));
  }
}

export async function getClientsList(state, render): Promise<void> {
  try {
    const { list: clientsList } = await apiCall('/clients/get-clients-list', { INCLUDE_CITIES: true, INCLUDE_STATES: true });
    state.clientsListOpts = clientsList.map((client) => ({
      value: client.CLIENT_ID.toString(),
      name: client.NAME,
      cities: client.CITIES,
      states: client.STATES,
    }));
    state.selectedClientOpts = setValueState('filterClient');
    render();
  } catch (err) {
    console.log(err);
    toast.error(t('erroUnidades'));
  }
}

export async function getStatesList(state, render): Promise<void> {
  try {
    const { list: statesList } = await apiCall('/dac/get-states-list', {});
    state.statesListOpts = statesList.map((state) => ({
      value: state.id.toString(),
      name: state.name,
    }));
    state.selectedStateOpts = setValueState('filterStates');
    render();
  } catch (err) {
    console.log(err);
    toast.error(t('ErroEstados'));
  }
}

export async function getDeviceInfo(devId: string, state: { devInfo, isLoading: boolean }, setState: (props: { devInfo?, isLoading?: boolean }) => void): Promise<void> {
  if (!state.devInfo) {
    try {
      state.devInfo = await getCachedDevInfo(devId, {});
    } catch (err) {
      console.log(err);
      toast.error(t('houveErro'));
    }
  }
  setState({ isLoading: false });
}

export async function getCountry(state, render, typeValueName) {
  try {
    const { list: statesList } = await apiCall('/dac/get-countries-list', {});
    state.countryListOpts = statesList.map((state) => ({
      value: typeValueName?.includes('pais') ? state.name : state.id,
      name: state.name,
    }));
    render();
  } catch (err) {
    console.log(err);
    toast.error(t('ErroEstados'));
  }
}

export function ajustParams(param) {
  if (typeof param === 'string') {
    return [param];
  }
  return param;
}

export async function getSupervisorsList(state, render): Promise<void> {
  try {
    const { list: supervisors } = await apiCall('/clients/get-unit-supervisors', { UNIT_IDS: state.unitsListOpts?.map((x) => Number(x.value)) });
    const formattedData = supervisors.map((supervisor) => ({
      name: `${supervisor.NOME} ${supervisor.SOBRENOME}`,
      value: supervisor.USER_ID,
      unit_id: supervisor.UNIT_ID,
    }));

    const list = [] as { name: string; value: string; unit_ids: number[] }[];
    formattedData.forEach((supervisorOptions) => {
      const { name, value, unit_id } = supervisorOptions;
      const existingSupervisor = list.find((item) => item.name === name && item.value === value);

      if (existingSupervisor) {
        if (!existingSupervisor.unit_ids.includes(unit_id)) {
          existingSupervisor.unit_ids.push(unit_id);
        }
      } else {
        list.push({ name, value, unit_ids: [unit_id] });
      }
    });

    state.selectedSupervisorOpts = setValueState('filterSupervisor');
    state.supervisorListOpts = list;
    render();
  } catch (err) {
    console.log(err);
    toast.error(t('erroSupervisor'));
  }
}

export async function getClassesList(state, render): Promise<void> {
  try {
    const { list: classesList } = await apiCall('/clients/get-classes-list', {});

    const listClasses = [] as { name: string; value: string; }[];
    const listClassesNames = [] as { name: string; value: string; class: string, units: number[] }[];

    classesList.forEach((item) => {
      const exists = listClasses.some((listItem) => listItem.value === item.class.CLASS_TYPE);
      if (!exists) {
        listClasses.push({ name: item.class.CLASS_TYPE, value: item.class.CLASS_TYPE });
      }

      const existsName = listClassesNames.some((listItem) => listItem.class === item.class.CLASS_NAME);
      if (!existsName) {
        listClassesNames.push({
          name: item.class.CLASS_NAME,
          value: item.class.CLASS_NAME,
          class: item.class.CLASS_TYPE,
          units: item.units.map((unit) => unit.UNIT_ID),
        });
      }
    });

    state.classesNamesListOpts = listClassesNames;
    state.classesListOpts = listClasses;

    const selectedClass = setValueState('filterClass');
    state.selectedClassesOpts = selectedClass;
    state.selectedClassesNamesOpts = setValueState('filterClassName');
    state.classesNamesFilterListOpts = selectedClass.length > 0
      ? listClassesNames.filter((item) => selectedClass.some((selected) => selected === item.class)) : listClassesNames;
    render();
  } catch (err) {
    console.log(err);
    toast.error(t('erroClasse'));
  }
}

export async function getListCities(): Promise<{ value: string, name: string, stateId: number }[]> {
  try {
    const { list: citiesList } = await apiCall('/dac/get-cities-list', {});
    return citiesList.map((city) => ({
      value: city.id.toString(),
      name: city.name,
      stateId: city.stateId,
    }));
  } catch (err) {
    console.log(err);
    toast.error(t('ErroCidades'));
    return [];
  }
}

export async function getListUnits(clientId?: number): Promise<
  {
    value: string,
    name: string
    clientId: number,
    city: string,
    state: number,
  }[]> {
  try {
    const profile = getUserProfile();
    const { list: unitsList } = await apiCall('/clients/get-units-list-basic', {
      CLIENT_ID: clientId,
      INCLUDE_UNIT_STATUS: handleUnitStatusByProfileInstallation(profile),
    });

    return unitsList.map((unit) => ({
      value: unit.UNIT_ID.toString(),
      name: unit.UNIT_NAME,
      clientId: unit.CLIENT_ID,
      city: unit.CITY_ID,
      state: unit.STATE_ID,
      stateName: unit.STATE_NAME,
    }));
  } catch (err) {
    console.log(err);
    toast.error(t('erroUnidades'));
    return [];
  }
}

export async function getListStates(): Promise<{
  value: string,
  name: string
}[]> {
  try {
    const { list: statesList } = await apiCall('/dac/get-states-list', {});
    return statesList.map((state) => ({
      value: state.id.toString(),
      name: state.name,
    }));
  } catch (err) {
    console.log(err);
    toast.error(t('ErroEstados'));
    return [];
  }
}

export async function getListRoomTypes(clientId?: number): Promise<{
  value: string,
  name: string,
}[]> {
  try {
    const { rtypes } = await apiCall('/clients/get-roomtypes-list', { CLIENT_ID: clientId });

    return rtypes.map((rtype) => ({
      value: rtype.RTYPE_ID.toString(),
      name: rtype.RTYPE_NAME,
    }));
  } catch (err) {
    console.log(err);
    toast.error(t('erroTipoAmbientes'));
    return [];
  }
}

export async function getListClassesNames(): Promise<
  {
    name: string;
    value: string;
    class: string;
    units: number[];
  }[]
  > {
  try {
    const { list: classesList } = await apiCall(
      '/clients/get-classes-list',
      {},
    );

    const listClassesNames = [] as {
      name: string;
      value: string;
      class: string;
      units: number[];
    }[];

    for (const classItem of classesList) {
      const existsName = listClassesNames.some(
        (listItem) => listItem.class === classItem.class.CLASS_NAME,
      );
      if (!existsName) {
        listClassesNames.push({
          name: classItem.class.CLASS_NAME,
          value: classItem.class.CLASS_ID.toString(),
          class: classItem.class.CLASS_TYPE,
          units: classItem.units.map((unit) => unit.UNIT_ID),
        });
      }
    }

    return listClassesNames;
  } catch (err) {
    console.log(err);
    toast.error(t('erroClasse'));
    return [];
  }
}

export async function getListClients(): Promise<{
  value: string,
  name: string
}[]> {
  try {
    const { list: clientsList } = await apiCall('/clients/get-clients-list', {});

    return clientsList.map((client) => ({
      value: client.CLIENT_ID.toString(),
      name: client.NAME,
    }));
  } catch (err) {
    console.log(err);
    toast.error(t('erroClientes'));
    return [];
  }
}

export async function getListClientsCitiesStates(): Promise<{
  value: string,
  name: string,
  cities: string[],
  states: number[],
}[]> {
  try {
    const { list: clientsList } = await apiCall('/clients/get-clients-list', { INCLUDE_CITIES: true, INCLUDE_STATES: true });
    return clientsList.map((client) => ({
      value: client.CLIENT_ID.toString(),
      name: client.NAME,
      cities: client.CITIES ?? [],
      states: client.STATES ?? [],
    }));
  } catch (err) {
    console.log(err);
    toast.error(t('erroClientes'));
    return [];
  }
}

export function verifyArrayParameters<TypeArray extends unknown[]>(array?: TypeArray): TypeArray | undefined {
  return array && array.length > 0 ? array : undefined;
}

export function verifyArrayParametersWithNumber(array?: unknown[]): number[] | undefined {
  const result = verifyArrayParameters(array);
  if (!result) return undefined;

  return result.map((item) => Number(item));
}

export async function getAllFaults(): Promise<{ name: string; value: string; }[]> {
  const { defs } = await apiCall('/dac/get-fault-descs');
  const names = defs.map(({ name }) => name);
  const non_duplicated_names = [...new Set(names)].map((name) => ({ name, value: name }));
  return non_duplicated_names;
}

export async function getListDevicesByType(state, render, type: 'dac' | 'dut' | undefined) {
  try {
    const { list } = await apiCall('/devices/get-devices-list', {
      device: type,
    });

    state.devicesListOpts = list.map((dev) => ({
      value: dev.DEVICE_CODE,
      name: dev.DEVICE_CODE,
      clientId: dev.CLIENT_ID,
      unitId: dev.UNIT_ID,
    }));

    state.selectedDevicesOpts = setValueState('filterDevice');
    render();
  } catch (err) {
    console.log(err);
    toast.error(t('erroDispositivos'));
    return [];
  }
}

export function getUnitStatus(state, render) {
  state.unitStatusListOpts = [
    {
      value: 'OPERATION',
      name: t('operacao'),
    },
    {
      value: 'MODERNIZATION',
      name: t('modernizacao'),
    },
    {
      value: 'FORCED_UNINSTALL',
      name: t('desinstalacaoForcada'),
    },
    {
      value: 'DEACTIVATED',
      name: t('desmobilizada'),
    },
  ];
  render();
}

export function getDeviceStatus(state, render) {
  state.deviceStatusListOpts = [
    {
      value: 'OPERATION',
      name: t('operacao'),
    },
    {
      value: 'MACHINE_WITHOUT_ENERGY',
      name: t('maquinaDesernergizada'),
    },
    {
      value: 'FORCED_UNINSTALL',
      name: t('desinstalacaoForcada'),
    },
    {
      value: 'DEVICE_REMOVED',
      name: t('dispositivoRemovido'),
    },
  ];
  render();
}

export function getReasonsOfflineStatus() {
  return [
    {
      id: 1,
      nome: 'Modem/WiFi desenergizado totalmente',
      descricao: 'O modem 4G está desenergizado, impossibilitando o envio das telemetrias dos dispositivos para o Celsius 360.',
    },
    {
      id: 2,
      nome: 'Repetidor/WiFi desenergizado parcialmente',
      descricao: 'Há uma desenergização parcial nos roteadores WiFi da unidade, impossibilitando o envio das telemetrias de parte dos  dispositivos para o Celsius 360.',
    },
    {
      id: 3,
      nome: 'Máquina desenergizada',
      descricao: 'Os dispositivos utilizam a mesma fonte de energia da máquina de ar condicionado, que está desenergizada.',
    },
    {
      id: 4,
      nome: 'Problema no dispositivo',
      descricao: 'Os dispositivos estão em estado de falha.',
    },
    {
      id: 5,
      nome: 'Inconclusivo',
      descricao: 'Não foi possível determinar a causa exata da desconexão.',
    },
  ];
}

export function getReasonsOfflineStatusPopulateState(state, render) {
  const reasons = getReasonsOfflineStatus();

  state.reasonsListOpts = reasons.map((reason) => ({
    value: reason.nome,
    name: reason.nome,
  }));

  render();
}

export function getApplications() {
  return [
    { id: 'Controle AC Central', nome: 'Controle AC Central' },
    { id: 'Controle/Saúde de AC Split', nome: 'Controle/Saúde de AC Split' },
    { id: 'Monitoramento de temperatura ambiente', nome: 'Monitoramento de temperatura ambiente' },
    { id: 'Controle Iluminação', nome: 'Controle Iluminação' },
    { id: 'Saúde de AC central', nome: 'Saúde de AC central' },
    { id: 'Monitoramento de energia', nome: 'Monitoramento de energia' },
    { id: 'Monitoramento de água', nome: 'Monitoramento de água' },
    { id: 'Monitoramento de Nobreak/Iluminação', nome: 'Monitoramento de Nobreak/Iluminação' },
    { id: 'Monitoramento de Qualidade do Ar', nome: 'Monitoramento de Qualidade do Ar' },
    { id: 'Outros', nome: 'Outros' },
  ];
}

export function getApplicationsPopulateState(state, render) {
  const applications = getApplications();

  state.applicationsListOpts = applications.map((app) => ({
    value: app.id,
    name: app.nome,
  }));

  render();
}
